import { useState, useEffect, useRef } from 'react';
import { useAuthState } from '../hooks/useAuthState';
import { collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, limit, updateDoc, deleteDoc, Timestamp, setDoc, getDocs } from 'firebase/firestore';
import { doc, getDoc } from 'firebase/firestore';
import { db, storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getGroupMembers } from '../lib/firestore/groups';
import '../styles/ChatInterface.css';

const ChatInterface = ({ chatType, chatId, onBack }) => {
  const { user } = useAuthState();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [chatInfo, setChatInfo] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editText, setEditText] = useState('');
  const [typingUsers, setTypingUsers] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [showParticipants, setShowParticipants] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastReadRef = useRef(null);

  useEffect(() => {
    if (!user || !chatId) return;
    loadChatInfo();
    loadParticipants();
    updateLastRead();
  }, [user, chatType, chatId]);

  const loadParticipants = async () => {
    if (!chatId || !chatType) return;
    
    try {
      if (chatType === 'group') {
        // Load group members
        const members = await getGroupMembers(chatId);
        // Load user data for each member
        const participantsWithData = await Promise.all(
          members.map(async (member) => {
            try {
              const userDoc = await getDoc(doc(db, 'users', member.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  userId: member.userId,
                  firstName: userData.firstName,
                  lastName: userData.lastName,
                  profilePicture: userData.profilePicture,
                  role: member.role,
                };
              }
              return {
                userId: member.userId,
                firstName: '',
                lastName: '',
                profilePicture: null,
                role: member.role,
              };
            } catch (err) {
              console.error('Error loading participant data:', err);
              return {
                userId: member.userId,
                firstName: '',
                lastName: '',
                profilePicture: null,
                role: member.role,
              };
            }
          })
        );
        setParticipants(participantsWithData);
      } else if (chatType === 'event') {
        // Load event attendees (RSVPs with state 'going' or 'checked_in')
        const rsvpsRef = collection(db, 'events', chatId, 'rsvps');
        const rsvpsSnapshot = await getDocs(rsvpsRef);
        
        const attendees = [];
        rsvpsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.state === 'going' || data.state === 'checked_in') {
            attendees.push(doc.id);
          }
        });

        // Load user data for each attendee
        const participantsWithData = await Promise.all(
          attendees.map(async (userId) => {
            try {
              const userDoc = await getDoc(doc(db, 'users', userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  userId,
                  firstName: userData.firstName,
                  lastName: userData.lastName,
                  profilePicture: userData.profilePicture,
                  role: null,
                };
              }
              return {
                userId,
                firstName: '',
                lastName: '',
                profilePicture: null,
                role: null,
              };
            } catch (err) {
              console.error('Error loading participant data:', err);
              return {
                userId,
                firstName: '',
                lastName: '',
                profilePicture: null,
                role: null,
              };
            }
          })
        );
        setParticipants(participantsWithData);
      }
    } catch (error) {
      console.error('Error loading participants:', error);
    }
  };

  // Update last read timestamp when chat is opened or new messages arrive
  const updateLastRead = async () => {
    if (!user || !chatId) return;
    
    try {
      const lastReadPath = chatType === 'event'
        ? ['events', chatId, 'groupChatLastRead', user.uid]
        : ['groups', chatId, 'groupChatLastRead', user.uid];
      
      await setDoc(doc(db, ...lastReadPath), {
        timestamp: serverTimestamp(),
      }, { merge: true });
      
      lastReadRef.current = new Date();
    } catch (error) {
      console.error('Error updating last read:', error);
    }
  };

  // Update last read when new messages arrive (debounced)
  useEffect(() => {
    if (messages.length > 0) {
      const timeout = setTimeout(() => {
        updateLastRead();
      }, 1000); // Debounce to avoid too many writes
      return () => clearTimeout(timeout);
    }
  }, [messages.length, chatId, chatType, user]);

  const loadChatInfo = async () => {
    if (!chatId || !chatType) {
      console.error('🔴 [ChatInterface] Missing chatId or chatType:', { chatId, chatType });
      return;
    }
    
    try {
      console.log('🔵 [ChatInterface] Loading chat info:', { chatType, chatId });
      if (chatType === 'event') {
        const eventDoc = await getDoc(doc(db, 'events', chatId));
        console.log('🔵 [ChatInterface] Event doc exists:', eventDoc.exists());
        if (eventDoc.exists()) {
          const data = eventDoc.data();
          console.log('🔵 [ChatInterface] Event data:', data);
          setChatInfo({
            name: data.title || 'Event',
            type: 'event',
          });
        } else {
          console.error('🔴 [ChatInterface] Event not found:', chatId);
          setChatInfo({ name: 'Event Not Found', type: 'event' });
        }
      } else if (chatType === 'group') {
        const groupDoc = await getDoc(doc(db, 'groups', chatId));
        console.log('🔵 [ChatInterface] Group doc exists:', groupDoc.exists());
        if (groupDoc.exists()) {
          const data = groupDoc.data();
          console.log('🔵 [ChatInterface] Group data:', data);
          setChatInfo({
            name: data.name || 'Group',
            type: 'group',
          });
        } else {
          console.error('🔴 [ChatInterface] Group not found:', chatId);
          setChatInfo({ name: 'Group Not Found', type: 'group' });
        }
      }
    } catch (error) {
      console.error('🔴 [ChatInterface] Error loading chat info:', error);
      console.error('🔴 [ChatInterface] Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      setChatInfo({ name: 'Error Loading Chat', type: chatType });
    }
  };

  useEffect(() => {
    if (!chatId || !chatType) {
      console.log('🔴 [ChatInterface] Missing chatId or chatType, skipping message load');
      return;
    }

    console.log('🔵 [ChatInterface] Setting up message listener:', { chatType, chatId });
    // Determine collection path based on chat type
    // Use groupChatMessages as subcollection (not groupChat/messages)
    const messagesPath = chatType === 'event' 
      ? ['events', chatId, 'groupChatMessages']
      : ['groups', chatId, 'groupChatMessages'];

    const messagesRef = collection(db, ...messagesPath);
    const q = query(messagesRef, orderBy('createdAt', 'asc'));

    setLoading(true);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        console.log('🔵 [ChatInterface] Messages snapshot:', snapshot.size, 'messages');
        const messagesList = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          messagesList.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || Date.now()),
          });
        });
        console.log('🔵 [ChatInterface] Processed messages:', messagesList.length);
        setMessages(messagesList);
        setLoading(false);
        scrollToBottom();
      },
      (error) => {
        console.error('🔴 [ChatInterface] Error loading messages:', error);
        console.error('🔴 [ChatInterface] Error details:', {
          message: error.message,
          code: error.code,
          path: messagesPath
        });
        setLoading(false);
      }
    );

    return () => {
      console.log('🔵 [ChatInterface] Unsubscribing from messages');
      unsubscribe();
    };
  }, [chatType, chatId]);

  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // Handle typing indicator
  useEffect(() => {
    if (!user || !chatId) return;

    const typingPath = chatType === 'event'
      ? ['events', chatId, 'groupChatTyping', user.uid]
      : ['groups', chatId, 'groupChatTyping', user.uid];

    const typingRef = doc(db, ...typingPath);

    if (isTyping) {
      setDoc(typingRef, {
        userId: user.uid,
        timestamp: serverTimestamp(),
      }, { merge: true });
    } else {
      deleteDoc(typingRef).catch(() => {}); // Ignore errors if doc doesn't exist
    }
  }, [isTyping, user, chatId, chatType]);

  // Listen for typing indicators
  useEffect(() => {
    if (!chatId) return;

    // Typing indicators stored as documents, not subcollection
    // We'll query all typing docs by listening to parent and filtering
    const typingCollectionPath = chatType === 'event'
      ? ['events', chatId, 'groupChatTyping']
      : ['groups', chatId, 'groupChatTyping'];

    // For typing, we need to use a collectionGroup query or listen to parent
    // Since typing is per-user, we'll query the parent document's typing subcollection
    // Actually, typing should be a collection of user docs
    const typingRef = collection(db, ...typingCollectionPath);
    const unsubscribe = onSnapshot(typingRef, (snapshot) => {
      const typing = [];
      const now = Date.now();
      snapshot.forEach((doc) => {
        const data = doc.data();
        const timestamp = data.timestamp?.toDate ? data.timestamp.toDate().getTime() : 0;
        // Only show typing if within last 3 seconds
        if (data.userId !== user?.uid && (now - timestamp) < 3000) {
          typing.push(data.userId);
        }
      });
      setTypingUsers(typing);
    });

    return () => unsubscribe();
  }, [chatId, chatType, user]);

  // Handle typing detection
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    if (!isTyping) {
      setIsTyping(true);
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 1000);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !user || !chatId) return;

    setIsTyping(false);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    try {
      // Determine collection path
      const messagesPath = chatType === 'event' 
        ? ['events', chatId, 'groupChatMessages']
        : ['groups', chatId, 'groupChatMessages'];

      const messagesRef = collection(db, ...messagesPath);
      const messageData = {
        userId: user.uid,
        createdAt: serverTimestamp(),
      };
      
      if (newMessage.trim()) {
        messageData.text = newMessage.trim();
      }
      
      await addDoc(messagesRef, messageData);
      
      setNewMessage('');
      updateLastRead();
      scrollToBottom();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  const handleEditMessage = async (messageId, newText) => {
    if (!user || !chatId || !newText.trim()) return;

    try {
      const messagePath = chatType === 'event'
        ? ['events', chatId, 'groupChatMessages', messageId]
        : ['groups', chatId, 'groupChatMessages', messageId];

      await updateDoc(doc(db, ...messagePath), {
        text: newText.trim(),
        editedAt: serverTimestamp(),
      });

      setEditingMessageId(null);
      setEditText('');
    } catch (error) {
      console.error('Error editing message:', error);
      alert('Failed to edit message. Please try again.');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!user || !chatId || !confirm('Are you sure you want to delete this message?')) return;

    try {
      const messagePath = chatType === 'event'
        ? ['events', chatId, 'groupChatMessages', messageId]
        : ['groups', chatId, 'groupChatMessages', messageId];

      await deleteDoc(doc(db, ...messagePath));
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message. Please try again.');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !user || !chatId) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setUploadingFile(true);
    try {
      // Upload to Firebase Storage
      const fileRef = ref(storage, `chat-attachments/${chatType}/${chatId}/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);

      // Send message with attachment
      const messagesPath = chatType === 'event'
        ? ['events', chatId, 'groupChatMessages']
        : ['groups', chatId, 'groupChatMessages'];

      const messagesRef = collection(db, ...messagesPath);
      await addDoc(messagesRef, {
        userId: user.uid,
        text: '',
        attachment: {
          type: 'image',
          url: downloadURL,
          fileName: file.name,
        },
        createdAt: serverTimestamp(),
      });

      updateLastRead();
      scrollToBottom();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingFile(false);
    }
  };

  // Load user data for message display
  const [userCache, setUserCache] = useState({});
  
  const getUserData = async (userId) => {
    if (userCache[userId]) {
      return userCache[userId];
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const userData = {
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          profilePicture: data.profilePicture || null,
        };
        setUserCache(prev => ({ ...prev, [userId]: userData }));
        return userData;
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }

    return { firstName: '', lastName: '', profilePicture: null };
  };

  const formatTime = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (d.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (d.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
      });
    }
  };

  if (!chatId || !chatType) {
    return (
      <div className="chat-interface">
        <div className="chat-interface-error">
          <p>Invalid chat selected</p>
          <button onClick={onBack}>Go Back</button>
        </div>
      </div>
    );
  }

  if (loading && messages.length === 0) {
    return (
      <div className="chat-interface">
        <div className="chat-interface-header">
          <button className="chat-interface-back" onClick={onBack}>
            ←
          </button>
          <div className="chat-interface-title">
            <h3>Loading...</h3>
            <small>{chatType === 'event' ? 'Event Chat' : 'Group Chat'}</small>
          </div>
        </div>
        <div className="chat-interface-loading">
          <div className="loading-spinner">Loading messages...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-interface">
      {/* Header */}
      <div className="chat-interface-header">
        <button className="chat-interface-back" onClick={onBack}>
          ←
        </button>
        <div className="chat-interface-title">
          <h3>{chatInfo?.name || (loading ? 'Loading...' : 'Chat')}</h3>
          <small>{chatType === 'event' ? 'Event Chat' : 'Group Chat'}</small>
        </div>
        <button
          className="chat-participants-button"
          onClick={() => setShowParticipants(!showParticipants)}
          title="View participants"
        >
          👥 {participants.length}
        </button>
      </div>

      {/* Participants List */}
      {showParticipants && (
        <div className="chat-participants-panel">
          <div className="chat-participants-header">
            <h4>Participants ({participants.length})</h4>
            <button onClick={() => setShowParticipants(false)}>×</button>
          </div>
          <div className="chat-participants-list">
            {participants.map((participant) => {
              const displayName = participant.firstName && participant.lastName
                ? `${participant.firstName} ${participant.lastName}`.trim()
                : participant.firstName || participant.lastName || 'User';
              const initials = displayName
                .split(' ')
                .map(n => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2);
              
              return (
                <div key={participant.userId} className="chat-participant-item">
                  {participant.profilePicture ? (
                    <img 
                      src={participant.profilePicture} 
                      alt={displayName}
                      className="chat-participant-avatar"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="chat-participant-avatar chat-participant-initials">
                      {initials || '👤'}
                    </div>
                  )}
                  <div className="chat-participant-info">
                    <div className="chat-participant-name">
                      {participant.userId === user?.uid ? 'You' : displayName}
                    </div>
                    {participant.role && (
                      <div className="chat-participant-role">
                        {participant.role === 'owner' ? 'Leader' : 
                         participant.role === 'cohost' ? 'Cohost' : 
                         'Member'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="chat-interface-messages" ref={messagesContainerRef}>
        {messages.length === 0 ? (
          <div className="chat-interface-empty">
            <p>No messages yet</p>
            <small>Start the conversation!</small>
          </div>
        ) : (
          messages.map((message, index) => {
            const isOwnMessage = message.userId === user?.uid;
            const prevMessage = index > 0 ? messages[index - 1] : null;
            // Show avatar if it's a new sender or 5+ minutes have passed
            const timeDiff = prevMessage && message.createdAt && prevMessage.createdAt
              ? (new Date(message.createdAt).getTime() - new Date(prevMessage.createdAt).getTime()) / 1000 / 60
              : Infinity;
            const showAvatar = !prevMessage || prevMessage.userId !== message.userId || timeDiff > 5;
            const showDate = !prevMessage || 
              formatDate(prevMessage.createdAt) !== formatDate(message.createdAt);

            return (
              <MessageBubble
                key={message.id}
                message={message}
                isOwn={isOwnMessage}
                showAvatar={showAvatar}
                showDate={showDate}
                getUserData={getUserData}
                formatTime={formatTime}
                formatDate={formatDate}
                user={user}
                onEdit={(id, text) => {
                  setEditingMessageId(id);
                  setEditText(text);
                }}
                onDelete={handleDeleteMessage}
                editingMessageId={editingMessageId}
                editText={editText}
                onEditSave={handleEditMessage}
                onEditCancel={() => {
                  setEditingMessageId(null);
                  setEditText('');
                }}
                setEditText={setEditText}
              />
            );
          })
        )}
        
        {/* Typing Indicator */}
        {typingUsers.length > 0 && (
          <div className="chat-typing-indicator">
            <span className="typing-dots">
              {typingUsers.length === 1 ? 'Someone is typing' : `${typingUsers.length} people are typing`}
              <span className="dot">.</span>
              <span className="dot">.</span>
              <span className="dot">.</span>
            </span>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form className="chat-interface-input" onSubmit={handleSendMessage}>
        <input
          type="file"
          id="chat-file-upload"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileUpload}
          disabled={uploadingFile}
        />
        <label htmlFor="chat-file-upload" className="chat-upload-button" title="Upload image">
          {uploadingFile ? '⏳' : '📷'}
        </label>
        <div className="chat-input-wrapper">
          <input
            type="text"
            className="chat-input-field"
            placeholder="Type a message..."
            value={newMessage}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (newMessage.trim()) {
                  handleSendMessage(e);
                }
              }
            }}
            autoFocus
            disabled={uploadingFile}
          />
          <button
            type="submit"
            className="chat-send-button"
            disabled={!newMessage.trim() || uploadingFile}
            title="Send message"
          >
            {newMessage.trim() ? '➤' : '⏎'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Message Bubble Component
const MessageBubble = ({ 
  message, 
  isOwn, 
  showAvatar, 
  showDate, 
  getUserData, 
  formatTime, 
  formatDate,
  user,
  onEdit,
  onDelete,
  editingMessageId,
  editText,
  onEditSave,
  onEditCancel,
  setEditText,
}) => {
  const [userData, setUserData] = useState(null);
  const [ownUserData, setOwnUserData] = useState(null);
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    if (isOwn && user) {
      getUserData(user.uid).then(setOwnUserData);
    } else {
      getUserData(message.userId).then(setUserData);
    }
  }, [message.userId, isOwn, user, getUserData]);

  const displayName = userData 
    ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Friend'
    : 'Friend';

  const isEditing = editingMessageId === message.id;

  return (
    <>
      {showDate && (
        <div className="chat-date-divider">
          {formatDate(message.createdAt)}
        </div>
      )}
      <div 
        className={`chat-message ${isOwn ? 'own' : ''}`}
        onMouseEnter={() => isOwn && setShowMenu(true)}
        onMouseLeave={() => setShowMenu(false)}
      >
        {!isOwn && showAvatar && (
          <div className="chat-message-avatar">
            {userData?.profilePicture ? (
              <img src={userData.profilePicture} alt={displayName} />
            ) : (
              <span>{displayName.charAt(0).toUpperCase()}</span>
            )}
          </div>
        )}
        {isOwn && showAvatar && (
          <div className="chat-message-avatar">
            {ownUserData?.profilePicture ? (
              <img src={ownUserData.profilePicture} alt="You" />
            ) : (
              <span>{(ownUserData?.firstName || ownUserData?.lastName || 'You').charAt(0).toUpperCase()}</span>
            )}
          </div>
        )}
        <div className="chat-message-content">
          {!isOwn && (
            <div className="chat-message-author">{displayName}</div>
          )}
          {isEditing ? (
            <div className="chat-message-edit">
              <input
                type="text"
                className="chat-edit-input"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    onEditSave(message.id, editText);
                  } else if (e.key === 'Escape') {
                    onEditCancel();
                  }
                }}
              />
              <div className="chat-edit-actions">
                <button
                  className="chat-edit-save"
                  onClick={() => onEditSave(message.id, editText)}
                >
                  Save
                </button>
                <button
                  className="chat-edit-cancel"
                  onClick={onEditCancel}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="chat-message-bubble">
                {message.attachment && (
                  <div className="chat-message-attachment">
                    <img 
                      src={message.attachment.url} 
                      alt={message.attachment.fileName || 'Attachment'} 
                      className="chat-attachment-image"
                    />
                  </div>
                )}
                {message.text && <div className="chat-message-text">{message.text}</div>}
                {message.editedAt && (
                  <span className="chat-message-edited">(edited)</span>
                )}
              </div>
              <div className="chat-message-time">
                {formatTime(message.createdAt)}
                {isOwn && showMenu && (
                  <div className="chat-message-menu">
                    <button
                      className="chat-message-menu-item"
                      onClick={() => onEdit(message.id, message.text || '')}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      className="chat-message-menu-item"
                      onClick={() => onDelete(message.id)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ChatInterface;

